<!--
*  TTTech nerve-ui-components
*  Copyright(c) 2022. TTTech Industrial Automation AG.
*
*  ALL RIGHTS RESERVED.
*
*  Usage of this software, including source code, netlists, documentation,
*  is subject to restrictions and conditions of the applicable license
*  agreement with TTTech Industrial Automation AG or its affiliates.
*
*  All trademarks used are the property of their respective owners.
*
*  TTTech Industrial Automation AG and its affiliates do not assume any liability
*  arising out of the application or use of any product described or shown
*  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
*  make changes, at any time, in order to improve reliability, function or
*  design.
*
*  Contact Information:
*  support@tttech-industrial.com
*
*  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
*
* -->
<template>
  <!-- eslint-disable vue/valid-v-slot -->
  <div>
    <div v-if="!searchNotEnabled" class="search-width">
      <v-card-title>
        <v-text-field
          :id="`iiotNerveSimpleTableSearch${id}`"
          v-model="search"
          v-resize="onResize"
          :label="$t('nerveSimpleTable.search')"
          :style="searchStyle"
          clearable
          prepend-inner-icon="mdi-magnify"
          hide-details
          class="default-table-search default-search-input ml-n4 font-class-name"
          @input="setSearchValue"
        />
      </v-card-title>
    </div>
    <v-data-table
      :id="`iiotNerveSimpleTable${id}`"
      :headers="headers"
      :items="items"
      :search="search"
      :no-data-text="emptyState"
      :no-results-text="emptyState"
      class="pointer default-nerve-simple-table"
      :hide-default-header="hideDefaultHeader"
      :class="{ emptyTableText: hideDefaultHeader }"
      fixed-header
      mobile-breakpoint="0"
      hide-default-footer
      dense
      disable-pagination
    >
      <template #header.file="{ header }">
        <span>{{ header.text }}</span>
        <v-tooltip v-if="header.value === 'file'" bottom>
          <template #activator="{ on }">
            <v-icon id="iiotNerveSimpleTableInfoIcon" class="cursor-help mb-2 ml-2" color="primary" small v-on="on">
              info
            </v-icon>
          </template>
          <span>{{ $t('workloadVersion.services.fileInfoMessage') }}</span>
        </v-tooltip>
      </template>
      <template #item="{ item, index }">
        <tr
          :id="`iiotNerveSimpleTable${id}Row${index}`"
          :class="getRowClasses(item, index)"
          @click="rowClick(item, index)"
        >
          <td v-for="header in headers" :key="header.value">
            <div v-if="header.component">
              <component
                :is="header.component.sfc"
                :id="`${id}Select`"
                v-bind="{ ...header.component.props, tableItem: item, index }"
                :bus="bus"
              />
            </div>
            <div v-else-if="header.length">
              {{ item[header.value].length }}
            </div>
            <div v-else>
              <div @mouseover="isTooltip($event)">
                <v-tooltip v-if="tooltip" bottom>
                  <template #activator="{ on }">
                    <div class="tableItemText" v-on="on">
                      {{ handleItem(item, header.value) }}
                    </div>
                  </template>
                  <span>{{ handleItem(item, header.value) }}</span>
                </v-tooltip>
                <div v-else class="tableItemText">
                  {{ handleItem(item, header.value) }}
                </div>
              </div>
            </div>
          </td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script>
// eslint-disable-next-line import/no-unresolved, import/no-extraneous-dependencies
import Vue from 'vue';
// eslint-disable-next-line import/extensions
import Helper from './helpers/shared.js';

export default {
  props: {
    id: {
      type: String,
      default: '',
    },
    headers: {
      type: Array,
      default: () => [],
    },
    items: {
      type: Array,
      default: () => [],
    },
    emptyState: {
      type: String,
      required: true,
    },
    bus: {
      type: Object,
      default: () => new Vue(),
    },
    rowClasses: {
      type: Function,
      default: () => {},
    },
    hideDefaultHeader: {
      type: Boolean,
      default: false,
    },
    searchNotEnabled: {
      type: Boolean,
      default: false,
    },
    disableClick: {
      type: Boolean,
      default: false,
    },
    store: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      search: '',
      maxWidth: 100,
      tooltip: true,
    };
  },
  computed: {
    searchStyle() {
      return {
        '--max-width': `${this.maxWidth}%`,
      };
    },
  },
  mounted() {
    if (this.store) {
      this.search = this.$store.getters[`${this.store}/getTableSearchValue`];
    }
    this.bus.$on('tableAction', (data) => {
      this.$emit('tableAction', data);
    });
  },

  methods: {
    // tooltip will be shown only in the case when text is too big and when contains 3 dots
    isTooltip(event) {
      this.tooltip = event.target.scrollWidth > event.target.offsetWidth;
    },
    rowClick(item, index) {
      if (this.disableClick) {
        return;
      }
      this.$emit('st-row-clicked', item, index);
    },
    // Function for setting classes to each row separately
    // rowClasses is function received as prop from parent component
    // eslint-disable-next-line consistent-return
    getRowClasses(item, index) {
      if (this.rowClasses && typeof this.rowClasses === 'function') {
        return this.rowClasses(item, index);
      }
    },
    /**
     * Enable user to use dot notation ('key.subKey') to
     * handle nested data of the table entity for arrays
     * (concatenate values) and objects.
     *
     * @param item - table item
     * @param key - column key
     * @returns {string|*}
     */
    handleItem(item, key) {
      const splitKeyArray = key.split('.');
      // value is first nested property
      const value = item[splitKeyArray[0]];

      if (Array.isArray(value)) {
        return this.handleArrayItemType(value, splitKeyArray[1]);
      }
      if (Helper.isObject(value)) {
        return value[splitKeyArray[1]];
      }

      return item[splitKeyArray[0]];
    },
    onResize() {
      if (window.innerWidth > 600) {
        this.maxWidth = 100;
        return;
      }
      this.maxWidth = 70;
    },
    setSearchValue() {
      if (this.store) {
        this.$store.dispatch(`${this.store}/set_table_search_value`, this.search);
      }
    },
  },
};
</script>
<style lang="scss">
.default-table-search {
  max-width: var(--max-width);
}
.search-width {
  width: 300px;
}
.primary-tttech-background-btn {
  background-image: linear-gradient(to bottom, var(--v-primary-base), var(--v-primary-darken1));
}
[id^='iiotNerveSimpleTable'] {
  ::-webkit-scrollbar {
    width: 3px;
    height: 3px;
  }

  ::-webkit-scrollbar-track {
    background: silver;
  }

  ::-webkit-scrollbar-thumb {
    @extend .primary-tttech-background-btn;
  }
}
.pointer {
  cursor: pointer !important;
}
.default-nerve-simple-table > .v-data-table__wrapper > table > tbody {
  tr td:first-child {
    border-left: solid 1px #f0f3f5 !important;
  }
  tr td:last-child {
    border-right: solid 1px #f0f3f5 !important;
  }
}
.default-nerve-simple-table > .v-data-table__wrapper > table > thead > tr > th > span {
  width: 100px;
  text-transform: none;
}
.default-nerve-simple-table > {
  .v-data-table__wrapper {
    overflow-y: auto;
    overflow-x: auto;
    max-height: 46vh;
    table {
      width: 100%;
    }
  }
  .v-data-table__wrapper > table {
    border-collapse: separate;
    border-spacing: 0 2px !important;
    border-bottom: solid 1px #f0f3f5 !important;

    tr {
      background-color: rgba(251, 252, 252, 1);
      border-radius: 3px;
      &:hover {
        background-color: rgba(226, 243, 255, 0.6) !important;
      }
      &:focus {
        outline: none !important;
      }

      td,
      th {
        border: none !important;
      }
    }
    .availableFiles:hover {
      background-color: rgb(182, 231, 182) !important;
    }
    .failedFiles:hover {
      background-color: rgb(223, 184, 172) !important;
    }
  }
}
.default-nerve-simple-table .v-data-table__wrapper > table tbody tr td {
  font-size: 14px !important;
}
.font-class-name * {
  font-size: 16px !important;
}
.mdi-magnify::before {
  font-size: 24px;
}
.default-nerve-simple-table > .v-data-table__wrapper > table > tbody {
  display: table-row-group;
}
.v-data-table-header th {
  white-space: nowrap;
}
.v-data-table-header-mobile__wrapper {
  width: 200px;
}
.theme--light.v-data-table.v-data-table--fixed-header thead th {
  box-shadow: inset 0 -1px 0 rgb(0 0 0 / 8%) !important;
}
.tableItemText {
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}
.emptyTableText.theme--light.v-data-table .v-data-table__empty-wrapper {
  text-align: start !important;
}
</style>
